import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { Link } from '@tanstack/react-router';
import classNames from 'classnames';
import type React from 'react';
import styles from './index.module.scss';

import { UserEnrollmentStatus } from '@/components/UserEnrollmentStatus';
import { ImportantNotificationBlock } from '@/routes/_embaUserLayout/-components/ImportantNotificationBlock';
import { ProgressBar } from '@/routes/_embaUserLayout/-components/ProgressBar';

export const ENDED_USER_ENROLLMENT = graphql(/* GraphQL */ `
  fragment EndedUserEnrollment on UserEnrollment {
    id
    startDateText
    endDateText
    scheduleStatus
    scheduleStatusText
    enrollmentCourses{
      id
      status
      statusText
      embaCourse {
        id
        name
        thumbnailImageUrl
      }
      attendances {
        id
        attendedCountText
        progress
        status
        certificate {
          fileRedirectUrl
        }
      }
    }
  }
`);

export const IMPORTANT_NOTIFICATION = graphql(/* GraphQL */ `
  fragment EmbaImportantNotification on ImportantNotification {
    id
    description
    url
    publishAtFromText
  }
`);

export type EndedUserEnrollmentPageProps = {
  currentEmbaUserEnrollment: FragmentType<typeof ENDED_USER_ENROLLMENT>;
  importantNotification: FragmentType<typeof IMPORTANT_NOTIFICATION> | null;
};

export const EndedUserEnrollmentPage = ({
  currentEmbaUserEnrollment,
  importantNotification,
}: EndedUserEnrollmentPageProps) => {
  const embaUserEnrollmentCoursesData = useFragment(
    ENDED_USER_ENROLLMENT,
    currentEmbaUserEnrollment,
  );
  const importantNotificationData = useFragment(
    IMPORTANT_NOTIFICATION,
    importantNotification,
  );

  return (
    <div className={styles.embaUserEnrollmentCourses}>
      {importantNotificationData && (
        <ImportantNotificationBlock
          dateStr={importantNotificationData.publishAtFromText}
          description={importantNotificationData.description}
          url={importantNotificationData.url}
        />
      )}
      <UserEnrollmentStatus
        scheduleStatus={embaUserEnrollmentCoursesData.scheduleStatus}
        scheduleStatusText={embaUserEnrollmentCoursesData.scheduleStatusText}
        enrollmentCoursesLength={
          embaUserEnrollmentCoursesData.enrollmentCourses.length
        }
        startDateText={embaUserEnrollmentCoursesData.startDateText}
        endDateText={embaUserEnrollmentCoursesData.endDateText}
      />
      <ul className={styles.courseList}>
        {embaUserEnrollmentCoursesData.enrollmentCourses.map(
          (enrollmentCourse) => (
            <li key={enrollmentCourse.id} className={styles.enrollmentCourse}>
              <div className={styles.courseInfo}>
                <h3>
                  {enrollmentCourse.status === 'completed' && (
                    <span className={styles.completedStatusIcon}>
                      {enrollmentCourse.statusText}
                    </span>
                  )}

                  <p className={styles.courseTitle}>
                    {enrollmentCourse.embaCourse.name}
                  </p>
                </h3>
                <div className={styles.attendanceArea}>
                  <span className={styles.courseImageArea}>
                    <img
                      src={enrollmentCourse.embaCourse.thumbnailImageUrl}
                      alt=""
                    />
                  </span>
                  <ul className={styles.attendances}>
                    {enrollmentCourse.attendances.map((attendance) => (
                      <li key={attendance.id}>
                        <div className={styles.progress}>
                          <p className={styles.attendedCount}>
                            {attendance.attendedCountText}
                          </p>
                          <ProgressBar progress={attendance.progress} />
                        </div>
                        <div className={styles.linkArea}>
                          <Link
                            to={attendance.certificate?.fileRedirectUrl}
                            target="_blank"
                            className={classNames(styles.actionLinkButton, {
                              [styles.disabled]: !attendance.certificate,
                            })}
                            disabled={!attendance.certificate}
                          >
                            修了証を見る
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </li>
          ),
        )}
      </ul>
    </div>
  );
};
